const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.15;
const INITIAL_QUALITY = 0.9;

export type ResizeOptions = {
  maxLongEdgePx: number;
  maxOutputBytes: number;
  mimeType?: string;
};

export type ResizeResult = {
  blob: Blob;
  width: number;
  height: number;
};

type DrawArgs = {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  mimeType: string;
  quality: number;
};

const computeTargetDimensions = (
  width: number,
  height: number,
  maxLongEdgePx: number
): { width: number; height: number } => {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdgePx) return { width, height };
  const scale = maxLongEdgePx / longEdge;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  };
};

const drawToBlob = async (args: DrawArgs): Promise<Blob> => {
  const { bitmap, width, height, mimeType, quality } = args;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.drawImage(bitmap, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob returned null"));
      },
      mimeType,
      quality
    );
  });
};

type CompressArgs = {
  bitmap: ImageBitmap;
  target: { width: number; height: number };
  mimeType: string;
  maxOutputBytes: number;
};

const compressUntilUnderCap = async (args: CompressArgs): Promise<Blob> => {
  const { bitmap, target, mimeType, maxOutputBytes } = args;
  let blob: Blob | null = null;
  let quality = INITIAL_QUALITY;
  while (quality >= MIN_QUALITY) {
    // Sequential by design — we stop early once a quality level fits the cap.
    // eslint-disable-next-line no-await-in-loop
    blob = await drawToBlob({
      bitmap,
      width: target.width,
      height: target.height,
      mimeType,
      quality
    });
    if (blob.size <= maxOutputBytes) return blob;
    quality -= QUALITY_STEP;
  }
  if (!blob) throw new Error("Resize produced no output");
  return blob;
};

export const resizeImage = async (file: File, options: ResizeOptions): Promise<ResizeResult> => {
  const mimeType = options.mimeType ?? "image/jpeg";

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (cause) {
    throw new Error("Could not decode image", { cause });
  }

  try {
    // Pixel-bomb guard: a small (~9 MB) PNG with extreme dimensions
    // (e.g. 60_000 × 60_000 px) decodes to ~14 GB of bitmap memory and OOMs
    // the tab. The byte-size gate in process-photo.ts caps INPUT bytes but
    // not pixel area. Reject anything beyond MAX_PIXEL_AREA before any
    // further allocation.
    const MAX_PIXEL_AREA = 100_000_000; // 100 megapixels — generous for phone cameras (50–60 MP today)
    if (bitmap.width * bitmap.height > MAX_PIXEL_AREA) {
      bitmap.close();
      throw new Error("Image dimensions exceed safe pixel limit");
    }

    const target = computeTargetDimensions(bitmap.width, bitmap.height, options.maxLongEdgePx);

    const needsResize = target.width !== bitmap.width || target.height !== bitmap.height;
    const exceedsByteCap = file.size > options.maxOutputBytes;

    if (!needsResize && !exceedsByteCap) {
      return { blob: file, width: bitmap.width, height: bitmap.height };
    }

    const blob = await compressUntilUnderCap({
      bitmap,
      target,
      mimeType,
      maxOutputBytes: options.maxOutputBytes
    });

    if (blob.size > options.maxOutputBytes) {
      throw new Error("Image is too large even after resize");
    }

    return { blob, width: target.width, height: target.height };
  } finally {
    bitmap.close?.();
  }
};
